import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Search, Pencil, Trash2, Download, X } from "lucide-react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TagsInput from "@/components/TagsInput";
import PhotoUpload from "@/components/PhotoUpload";
import PhotoLightbox from "@/components/PhotoLightbox";
import { toast } from "sonner";

// helper: ISO -> value for datetime-local input
function isoToLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localToIso(local) {
  if (!local) return "";
  return new Date(local).toISOString();
}

const EMPTY = { pesantren_id: "", tanggal: "", topik: "", catatan: "", peserta: [], foto: [] };
const PAGE_LIMIT = 20;

export default function Kunjungan() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [nextOffset, setNextOffset] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [pesantrenList, setPesantrenList] = useState([]);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [pesantrenFilter, setPesantrenFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [lightbox, setLightbox] = useState({ photos: [], index: null });
  const sentinelRef = useRef(null);

  const openLightbox = (photos, index) => setLightbox({ photos, index });
  const closeLightbox = () => setLightbox({ photos: [], index: null });

  const buildParams = useCallback(
    (offset) => {
      const params = { offset, limit: PAGE_LIMIT };
      if (q) params.q = q;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (pesantrenFilter !== "ALL") params.pesantren_id = pesantrenFilter;
      return params;
    },
    [q, dateFrom, dateTo, pesantrenFilter]
  );

  const loadFirst = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/kunjungan", { params: buildParams(0) });
      setItems(data.items);
      setTotal(data.total);
      setNextOffset(data.next_offset);
      setHasMore(data.has_more);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || nextOffset == null) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/kunjungan", { params: buildParams(nextOffset) });
      setItems((prev) => [...prev, ...data.items]);
      setNextOffset(data.next_offset);
      setHasMore(data.has_more);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoadingMore(false);
    }
  }, [buildParams, hasMore, loadingMore, nextOffset]);

  const loadPesantrenList = async () => {
    const { data } = await api.get("/pesantren", { params: { page_size: 500 } });
    setPesantrenList(data.items);
  };

  useEffect(() => {
    loadPesantrenList();
  }, []);

  useEffect(() => {
    const t = setTimeout(loadFirst, 300);
    return () => clearTimeout(t);
  }, [loadFirst]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore]);

  const openCreate = () => {
    if (pesantrenList.length === 0) {
      toast.error("Tambah objek Mad’u terlebih dahulu");
      return;
    }
    setEditing(null);
    setForm({ ...EMPTY, tanggal: isoToLocal(new Date().toISOString()) });
    setDialogOpen(true);
  };

  const openEdit = (k) => {
    setEditing(k);
    setForm({
      pesantren_id: k.pesantren_id,
      tanggal: isoToLocal(k.tanggal),
      topik: k.topik,
      catatan: k.catatan || "",
      peserta: k.peserta || [],
      foto: k.foto || [],
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.pesantren_id) {
      toast.error("Pilih pesantren");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, tanggal: localToIso(form.tanggal) };
      if (editing) {
        await api.put(`/kunjungan/${editing.id}`, payload);
        toast.success("Kunjungan diperbarui");
      } else {
        await api.post("/kunjungan", payload);
        toast.success("Kunjungan ditambahkan");
      }
      setDialogOpen(false);
      await loadFirst();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/kunjungan/${deleteTarget.id}`);
      toast.success("Kunjungan dihapus");
      setDeleteTarget(null);
      await loadFirst();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const doExport = async () => {
    try {
      const token = localStorage.getItem("rabtah_token");
      const res = await fetch(`${API_BASE}/kunjungan/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export gagal");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "kunjungan.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("File CSV diunduh");
    } catch (err) {
      toast.error("Gagal export");
    }
  };

  const clearFilters = () => {
    setQ("");
    setDateFrom("");
    setDateTo("");
    setPesantrenFilter("ALL");
  };

  const hasActiveFilter = q || dateFrom || dateTo || pesantrenFilter !== "ALL";

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 md:mb-10">
        <div>
          <div className="label-overline">Laporan</div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] mt-2">
            Kunjungan
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            data-testid="kunjungan-export"
            className="rounded-none border-black hover:bg-black hover:text-white"
            onClick={doExport}
          >
            <Download size={14} className="mr-2" /> Export CSV
          </Button>
          <Button
            data-testid="add-kunjungan-btn"
            className="rounded-none bg-black text-white hover:bg-white hover:text-black hover:border hover:border-black"
            onClick={openCreate}
          >
            <Plus size={14} className="mr-2" /> Tambah
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="relative md:col-span-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            data-testid="kunjungan-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari topik atau catatan..."
            className="pl-10 h-11 rounded-none border-black"
          />
        </div>
        <div>
          <Label className="label-overline">Objek Mad’u</Label>
          <Select value={pesantrenFilter} onValueChange={setPesantrenFilter}>
            <SelectTrigger data-testid="kunjungan-pesantren-filter" className="mt-1 rounded-none border-black h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none max-h-72">
              <SelectItem value="ALL">Semua Objek</SelectItem>
              {pesantrenList.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-overline">Dari Tanggal</Label>
          <Input
            type="date"
            data-testid="kunjungan-date-from"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="mt-1 rounded-none border-black h-11"
          />
        </div>
        <div>
          <Label className="label-overline">Sampai Tanggal</Label>
          <Input
            type="date"
            data-testid="kunjungan-date-to"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="mt-1 rounded-none border-black h-11"
          />
        </div>
        <div className="flex items-end">
          <button
            data-testid="kunjungan-clear-filters"
            onClick={clearFilters}
            disabled={!hasActiveFilter}
            className="h-11 w-full border border-black text-xs uppercase tracking-wider disabled:opacity-30 hover:bg-black hover:text-white transition-colors duration-150 disabled:hover:bg-transparent disabled:hover:text-black flex items-center justify-center gap-2"
          >
            <X size={14} /> Reset
          </button>
        </div>
      </div>

      <div className="label-overline mb-3" data-testid="kunjungan-total">
        {total} kunjungan {hasActiveFilter ? "(terfilter)" : "total"}
      </div>

      {loading ? (
        <div className="label-overline text-center py-16">Memuat...</div>
      ) : items.length === 0 ? (
        <div className="border border-neutral-200 p-12 text-center">
          <div className="font-display font-bold text-xl mb-2">
            {hasActiveFilter ? "Tidak ada hasil" : "Belum ada kunjungan"}
          </div>
          <div className="text-sm text-neutral-500">
            {hasActiveFilter ? "Coba ubah filter." : "Catat kunjungan pertama Anda ke objek Mad’u."}
          </div>
        </div>
      ) : (
        <>
          <div className="border border-neutral-200 divide-y divide-neutral-200" data-testid="kunjungan-list">
            {items.map((k) => (
              <div key={k.id} className="p-4 md:p-5" data-testid={`kunjungan-${k.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-lg tracking-tight">{k.pesantren_nama}</div>
                    <div className="label-overline mt-1">
                      {new Date(k.tanggal).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                    <div className="mt-2 text-sm font-medium">Topik: {k.topik}</div>
                    {k.catatan && <div className="mt-1 text-sm text-neutral-600">{k.catatan}</div>}
                    {k.peserta?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {k.peserta.map((p, i) => (
                          <span key={i} className="text-[0.65rem] uppercase tracking-wider border border-neutral-300 px-2 py-0.5">
                            {p}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      data-testid={`edit-kunjungan-${k.id}`}
                      onClick={() => openEdit(k)}
                      className="p-2 hover:bg-black hover:text-white transition-colors duration-150"
                      aria-label="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      data-testid={`delete-kunjungan-${k.id}`}
                      onClick={() => setDeleteTarget(k)}
                      className="p-2 hover:bg-black hover:text-white transition-colors duration-150"
                      aria-label="Hapus"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {k.foto?.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-neutral-100">
                    <div className="label-overline mb-2">Foto ({k.foto.length})</div>
                    <div
                      className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2"
                      data-testid={`kunjungan-photos-${k.id}`}
                    >
                      {k.foto.map((src, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => openLightbox(k.foto, idx)}
                          data-testid={`kunjungan-photo-${k.id}-${idx}`}
                          className="aspect-square border border-neutral-200 overflow-hidden bg-neutral-100 hover:border-black transition-colors duration-150"
                          aria-label={`Lihat foto ${idx + 1}`}
                        >
                          <img
                            src={src}
                            alt={`Foto ${idx + 1} - ${k.pesantren_nama}`}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="py-6 text-center" data-testid="kunjungan-sentinel">
            {loadingMore ? (
              <span className="label-overline">Memuat lebih banyak...</span>
            ) : hasMore ? (
              <button
                data-testid="kunjungan-load-more"
                onClick={loadMore}
                className="border border-black px-4 py-2 text-xs uppercase tracking-wider hover:bg-black hover:text-white transition-colors duration-150"
              >
                Muat Lebih Banyak
              </button>
            ) : items.length > 0 ? (
              <span className="label-overline text-neutral-400">— Akhir daftar —</span>
            ) : null}
          </div>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none border-black max-w-lg max-h-[90vh] overflow-y-auto" data-testid="kunjungan-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tight">
              {editing ? "Edit Kunjungan" : "Tambah Kunjungan"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="label-overline">Objek Mad’u *</Label>
              <Select value={form.pesantren_id} onValueChange={(v) => setForm({ ...form, pesantren_id: v })}>
                <SelectTrigger data-testid="input-pesantren" className="mt-1 rounded-none border-black h-11">
                  <SelectValue placeholder="Pilih objek Mad’u" />
                </SelectTrigger>
                <SelectContent className="rounded-none max-h-64">
                  {pesantrenList.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-overline">Tanggal & Waktu *</Label>
              <Input
                required
                type="datetime-local"
                data-testid="input-tanggal"
                value={form.tanggal}
                onChange={(e) => setForm({ ...form, tanggal: e.target.value })}
                className="mt-1 rounded-none border-black h-11"
              />
            </div>
            <div>
              <Label className="label-overline">Topik Pembahasan *</Label>
              <Input
                required
                data-testid="input-topik"
                value={form.topik}
                onChange={(e) => setForm({ ...form, topik: e.target.value })}
                className="mt-1 rounded-none border-black h-11"
              />
            </div>
            <div>
              <Label className="label-overline">Catatan Kunjungan</Label>
              <Textarea
                data-testid="input-catatan-kunjungan"
                value={form.catatan}
                onChange={(e) => setForm({ ...form, catatan: e.target.value })}
                className="mt-1 rounded-none border-black"
                rows={3}
              />
            </div>
            <div>
              <Label className="label-overline">Siapa yang Mengunjungi</Label>
              <div className="mt-1">
                <TagsInput
                  value={form.peserta}
                  onChange={(peserta) => setForm({ ...form, peserta })}
                />
              </div>
              <div className="text-[0.65rem] text-neutral-500 mt-1">Ketik nama lalu tekan Enter untuk menambahkan.</div>
            </div>
            <div>
              <Label className="label-overline">Foto Kunjungan</Label>
              <div className="mt-1">
                <PhotoUpload
                  value={form.foto}
                  onChange={(foto) => setForm({ ...form, foto })}
                  onPreview={(idx) => openLightbox(form.foto, idx)}
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                data-testid="kunjungan-cancel"
                className="rounded-none border-black"
                onClick={() => setDialogOpen(false)}
              >
                Batal
              </Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="kunjungan-save"
                className="rounded-none bg-black text-white hover:bg-white hover:text-black hover:border hover:border-black"
              >
                {saving ? "MENYIMPAN..." : "SIMPAN"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none border-black">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black tracking-tight">Hapus Kunjungan?</AlertDialogTitle>
            <AlertDialogDescription>Aksi ini tidak dapat dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-kunjungan-cancel" className="rounded-none border-black">Batal</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-kunjungan-confirm"
              onClick={confirmDelete}
              className="rounded-none bg-black text-white hover:bg-white hover:text-black hover:border hover:border-black"
            >
              HAPUS
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PhotoLightbox
        photos={lightbox.photos}
        index={lightbox.index}
        onIndexChange={(i) => setLightbox((s) => ({ ...s, index: i }))}
        onClose={closeLightbox}
      />
    </div>
  );
}
